import { SuspenseSelect } from '@/components/Suspense';
import {
  BaseSelectProps,
  useSetCacheQueryData,
} from '@/context/SelectComponent/Base';
import { useModelsToOptionsWithCacheItems } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { getPageData } from '@/utils/getPageData';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import {
  Currencies as CurrenciesModel,
  useGetCurrencyUnitsSuspenseInfinite,
} from 'ap-openapi';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<
    FormSelectProps<TFieldValues>['selectProps'],
    'options' | 'comboBox'
  >;
  query?: string;
  per?: number;
  validateMaxCount?: number;
  comboBox?: boolean;
  showLabel?: boolean;
  excludeJpy?: boolean;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'> &
  BaseSelectProps;

export const _CurrencyUnitSelect = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  cacheQueryKey,
  per = 25,
  comboBox = true,
  showLabel,
  excludeJpy,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const {
    data: apiData,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetCurrencyUnitsSuspenseInfinite(
    {
      query,
      per: per,
      exclude_jpy: excludeJpy,
    },
    {
      query: {
        getNextPageParam: (page) => {
          const url = new URL(page.data.next ?? '/', location.href);
          if (!url.searchParams.has('page')) return undefined;
          return Number(url.searchParams.get('page'));
        },
      },
    }
  );
  if (error) throw error;
  const data = useMemo(
    () =>
      getPageData(apiData, 'currencies').filter((item) =>
        query ? item.name?.toLowerCase()?.includes(query.toLowerCase()) : true
      ),
    [apiData, query]
  );
  const options = useModelsToOptionsWithCacheItems<CurrenciesModel>(
    data,
    cacheQueryKey,
    query,
    defaultOptions,
    {
      labelKeys: ['name'],
      value: 'id',
    }
  );
  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const label = useMemo(
    () =>
      showLabel
        ? {
            text: t('label_ap_currency'),
          }
        : undefined,
    [showLabel, t]
  );
  const selectProps: SelectProps = useMemo(
    () => ({
      onSearchDebounceWait: 600,
      comboBox: comboBox,
      placeholder: t('currencies_unit_placeholder'),
      inputPlaceholder: t('currencies_unit_input_placeholder'),
      ..._selectProps,
      options: options,
      onSearch: onSearch,
      loading: isLoading,
      mode: 'single',
      fetchMore: fetchMore,
      selectAll: undefined,
      selectAllLabel: undefined,
    }),
    [_selectProps, comboBox, fetchMore, isLoading, onSearch, options, t]
  );
  useSetCacheQueryData(cacheQueryKey, data);
  return <FormSelect selectProps={selectProps} label={label} {...rest} />;
};
_CurrencyUnitSelect.displayName = '_CurrencyUnitSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const CurrenciesSelect = memo((props) => (
  <SuspenseSelect>
    <_CurrencyUnitSelect {...props} />
  </SuspenseSelect>
)) as typeof _CurrencyUnitSelect;
CurrenciesSelect.displayName = 'CurrenciesSelect';
